// Term toggletip (plan 9.5.6, mockup ui.term / ui.termOnce / ui.label / openTerm): a dotted-underlined BUTTON
// (aria-expanded, aria-controls) that opens a small explanation without leaving the screen.
//   PC (> 760 px): non-modal popover next to the word; focus stays on the button, Tab moves into the popover's links,
//                  Tab past the last link continues after the button; Esc / ✕ close and return focus, a press outside closes.
//   Phone:         bottom sheet (role=dialog, modal) — focus goes inside.
// Inside: plain words + 1–2 sentence definition, [용어집에서 자세히] (same tab), [외부 설명 ↗] (language shown, new tab).
// Only the first appearance per card/panel gets the button (TermScope auto); with `once` also per screen.
import { createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import { docLangOf, plainOf, type GlossaryTerm } from '../glossary';
import { useGlossaryTerm } from '../glossary/useGlossaryTerm';
import { labelSlug, useLang, useT } from '../i18n';
import { isMobile } from '../lib/media';
import { clamp, defShort, mdPlain } from '../lib/util';
import { Icon } from './Icon';
import { hideTip, registerFloating } from './layers';
import { Modal } from './Modal';

// ───────────────────────── first-appearance scopes ─────────────────────────
type Registry = Map<string, string>;
const CardScope = createContext<Registry | null>(null);
const ScreenScope = createContext<Registry | null>(null);

/** Card / panel scope: the second appearance of the same term inside it is plain text (ui.termOnce). */
export function TermScope({ children, kind = 'card' }: { children: ReactNode; kind?: 'card' | 'screen' }) {
  // a fresh registry per render pass: the first Term rendered (document order) claims the button
  const registry: Registry = new Map();
  return kind === 'screen' ? <ScreenScope.Provider value={registry}>{children}</ScreenScope.Provider> : <CardScope.Provider value={registry}>{children}</CardScope.Provider>;
}

function claim(reg: Registry | null, slug: string, id: string): boolean {
  if (!reg) return true;
  const owner = reg.get(slug);
  if (owner === undefined) {
    reg.set(slug, id);
    return true;
  }
  return owner === id;
}

// ───────────────────────── toggletip body ─────────────────────────
export interface ToggletipExtra {
  /** Shown as the title (card help: the card question). Default: the term's plain words. */
  title?: string;
  /** Extra explanation sentence (card help). */
  text?: ReactNode;
  /** Calculation (card help). */
  formula?: ReactNode;
}

export function ToggletipBody({ g, extra }: { g: GlossaryTerm | null; extra?: ToggletipExtra }) {
  const t = useT();
  const lang = useLang();
  const plain = g ? plainOf(g, lang) : '';
  const dl = g ? docLangOf(g) : null;
  return (
    <>
      {g && extra?.title && extra.title !== plain ? <p className="tt__plain">{plain}</p> : null}
      {g ? (
        <p className="tt__orig">
          <span className="visually-hidden">{t('term.original')}: </span>
          <span data-term-source="original">{lang === 'en' ? g.termEn : g.term}</span>
        </p>
      ) : null}
      {extra?.text ? <p className="tt__def">{extra.text}</p> : null}
      {g ? (
        <p className="tt__def" data-term-source="def">
          {defShort(mdPlain(lang === 'en' ? g.def.en : g.def.ko))}
        </p>
      ) : null}
      {extra?.formula ? (
        <p className="tt__formula">
          <b>{t('card.formula')} </b>
          {extra.formula}
        </p>
      ) : null}
      <div className="tt__links">
        {g ? (
          <Link className="tt__more" to={`/glossary/${g.slug}`}>
            <Icon name="book" />
            {t('term.more')}
          </Link>
        ) : null}
        {g && g.url && g.linkStatus !== 'check_needed' ? (
          <a className="tt__ext" href={g.url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">
            {t('term.external')} ↗
            <small>
              {' · '}
              {t(`term.lang.${dl}`)}
              {g.urlLabel ? ' · ' + String(g.urlLabel).split(' · ')[0] : ''}
            </small>
          </a>
        ) : g && g.url ? (
          <small className="tt__muted">{t('term.linkHidden')}</small>
        ) : g && g.manualRef ? (
          <small className="tt__muted">{t('term.manual')}</small>
        ) : null}
      </div>
    </>
  );
}

// ───────────────────────── open / close ─────────────────────────
function focusNextAfter(el: HTMLElement) {
  // without layout (tests, print) fall back to the hidden attribute
  const hasLayout = document.documentElement.getClientRects().length > 0;
  const visible = (x: HTMLElement) => x === el || x.getClientRects().length > 0 || x.offsetParent !== null || (!hasLayout && !x.closest('[hidden]'));
  const all = Array.from(document.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input, select, textarea, [tabindex="0"]')).filter(visible);
  const i = all.indexOf(el);
  if (i >= 0 && all[i + 1]) all[i + 1]!.focus();
}

interface ToggletipState {
  open: boolean;
  mode: 'pop' | 'sheet';
}

/**
 * Toggletip behaviour for a button (terms and the card "?" help). Returns the open state, a toggle for onClick,
 * a keydown handler for the button (Tab hand-off) and the element to render.
 */
export function useToggletip(anchorRef: RefObject<HTMLElement | null>, g: GlossaryTerm | null, extra?: ToggletipExtra) {
  const t = useT();
  const lang = useLang();
  const tid = useId();
  const [st, setSt] = useState<ToggletipState>({ open: false, mode: 'pop' });
  const popRef = useRef<HTMLDivElement>(null);
  const title = extra?.title || (g ? plainOf(g, lang) : '');

  const close = useCallback(
    (returnFocus: boolean) => {
      setSt((s) => (s.open ? { ...s, open: false } : s));
      if (returnFocus && anchorRef.current && document.contains(anchorRef.current)) anchorRef.current.focus({ preventScroll: true });
    },
    [anchorRef],
  );
  const toggle = useCallback(() => {
    hideTip();
    setSt((s) => (s.open ? { ...s, open: false } : { open: true, mode: isMobile() ? 'sheet' : 'pop' }));
  }, []);

  const place = useCallback(() => {
    const pop = popRef.current;
    const btn = anchorRef.current;
    if (!pop || !btn) return;
    const r = btn.getBoundingClientRect();
    const pw = pop.offsetWidth;
    const ph = pop.offsetHeight;
    const vw = document.documentElement.clientWidth || window.innerWidth;
    const vh = window.innerHeight;
    const left = clamp(r.left + r.width / 2 - pw / 2, 8, Math.max(8, vw - pw - 8));
    let top = r.bottom + 10;
    if (top + ph > vh - 8 && r.top - 10 - ph > 8) top = r.top - 10 - ph;
    pop.style.left = `${left + window.scrollX}px`;
    pop.style.top = `${top + window.scrollY}px`;
  }, [anchorRef]);

  useLayoutEffect(() => {
    if (st.open && st.mode === 'pop') place();
  }, [st, place]);

  useEffect(() => {
    if (!st.open || st.mode !== 'pop') return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as Node;
      if (popRef.current?.contains(target) || anchorRef.current?.contains(target)) return;
      close(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      close(true);
    };
    const tm = setTimeout(() => document.addEventListener('pointerdown', onDown, true), 0);
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', place);
    const unregister = registerFloating(() => close(false));
    return () => {
      clearTimeout(tm);
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', place);
      unregister();
    };
  }, [st, close, place, anchorRef]);

  /** Button keydown: Tab goes into the open popover's first link. */
  const onAnchorKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key !== 'Tab' || e.shiftKey || !st.open || st.mode !== 'pop') return;
      const pop = popRef.current;
      const first = pop?.querySelector<HTMLElement>('.tt__links a[href]') || pop?.querySelector<HTMLElement>('a[href], button');
      if (first) {
        e.preventDefault();
        first.focus();
      }
    },
    [st],
  );

  const onPopKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key !== 'Tab' || !popRef.current) return;
    const items = Array.from(popRef.current.querySelectorAll<HTMLElement>('a[href], button'));
    if (e.shiftKey && document.activeElement === items[0]) {
      e.preventDefault();
      anchorRef.current?.focus();
    } else if (!e.shiftKey && document.activeElement === items[items.length - 1]) {
      e.preventDefault();
      const btn = anchorRef.current;
      close(false);
      if (btn) focusNextAfter(btn);
    }
  };

  let node: ReactNode = null;
  if (st.open && st.mode === 'pop' && typeof document !== 'undefined') {
    node = createPortal(
      <div ref={popRef} className="popover is-open tt" id={tid} role="dialog" aria-modal="false" aria-label={title} style={{ width: 320 }} onKeyDown={onPopKeyDown}>
        <div className="tt__head">
          <b className="tt__title">{title}</b>
          <button className="x-btn x-btn--sm" type="button" aria-label={t('common.close')} data-tip={t('common.close')} onClick={() => close(true)}>
            <Icon name="x" />
          </button>
        </div>
        <ToggletipBody g={g} extra={extra} />
      </div>,
      document.body,
    );
  } else if (st.mode === 'sheet') {
    node = (
      <Modal open={st.open} onClose={() => close(false)} kicker={t('term.plain')} title={title} className="sheet--panel tt-sheet">
        <div className="tt tt--sheet" id={tid}>
          <ToggletipBody g={g} extra={extra} />
        </div>
      </Modal>
    );
  }
  return { open: st.open, toggle, close, onAnchorKeyDown, node, controlsId: tid };
}

// ───────────────────────── Term ─────────────────────────
export interface TermProps {
  slug: string;
  /** Text to show (default: the term's plain words in the current language). */
  children?: ReactNode;
  /** Only the first appearance on the whole screen gets the button. */
  once?: boolean;
}

export function Term({ slug, children, once }: TermProps) {
  const lang = useLang();
  const id = useId();
  const card = useContext(CardScope);
  const screen = useContext(ScreenScope);
  const g = useGlossaryTerm(slug);
  const btnRef = useRef<HTMLButtonElement>(null);
  const tip = useToggletip(btnRef, g);
  const text = children ?? (g ? plainOf(g, lang) : slug);
  if (!g)
    return (
      <span className="term term--missing" data-term={slug}>
        {text}
      </span>
    );
  const first = claim(card, g.slug, id) && (!once || claim(screen, g.slug, id));
  if (!first)
    return (
      <span className="term-plain" data-term-plain={g.slug}>
        {text}
      </span>
    );
  return (
    <>
      <button
        ref={btnRef}
        className="term"
        type="button"
        data-term={g.slug}
        aria-expanded={tip.open}
        aria-controls={tip.controlsId}
        aria-haspopup="dialog"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          tip.toggle();
        }}
        onKeyDown={tip.onAnchorKeyDown}
      >
        {text}
      </button>
      {tip.node}
    </>
  );
}

/** Label dictionary text, with a term toggletip when the label has a glossary slug (ui.label). */
export function Label({ k, term = true, once }: { k: string; term?: boolean; once?: boolean }) {
  const t = useT();
  const text = t('label.' + k);
  const slug = labelSlug(k);
  if (!slug || !term) return <>{text}</>;
  return (
    <Term slug={slug} once={once}>
      {text}
    </Term>
  );
}
